import { Model } from '@/models/Model';
import { QueryClient, QueryKey } from '@tanstack/react-query';
import {
  AddCommand,
  CacheCommandI,
  MergeCommand,
  RemoveCommand,
  SetCommand,
  UpdateCommand,
} from './CacheCommand';
import {
  AddOptions,
  CacheStrategy,
  UpdateItem,
  UpdateOptions,
} from './strategy/CacheStrategy';
import CacheStrategyFactory, {
  CacheType,
} from './strategy/CacheStrategyFactory';

export interface CacheManagerOptions {
  queryKey: QueryKey;
  cacheType: CacheType;
  queryClient: QueryClient;
}

export class CacheManager<T extends Model> {
  protected strategy: CacheStrategy<T>;
  protected commandHistory: CacheCommandI<T>[] = [];

  constructor(options: CacheManagerOptions) {
    const queryClient = options.queryClient;
    this.strategy = CacheStrategyFactory.create<T>(
      options.cacheType,
      queryClient,
      options.queryKey,
    );
  }

  get(): T | T[] | undefined {
    return this.strategy.get();
  }

  remove(id: number): this {
    const command = new RemoveCommand(this.strategy, id);
    command.execute();
    this.commandHistory.push(command);
    return this;
  }

  set(data: T | T[]): this {
    const command = new SetCommand(this.strategy, data);
    command.execute();
    this.commandHistory.push(command);
    return this;
  }

  merge(data: Partial<T>): this {
    const command = new MergeCommand(this.strategy, data);
    command.execute();
    this.commandHistory.push(command);
    return this;
  }
  undo(): this {
    const command = this.commandHistory.pop();
    if (command) {
      command.undo?.();
    }
    return this;
  }

  batch(operations: (() => void)[]): this {
    operations.forEach(op => op());
    return this;
  }
}

export class ListCacheManager<T extends Model> extends CacheManager<T> {
  constructor(options: CacheManagerOptions) {
    super(options);
  }

  get(): T[] | undefined {
    return this.strategy.get() as T[] | undefined;
  }

  getById(id: number): T | undefined {
    return this.strategy.getById?.(id);
  }

  add(data: T, options?: AddOptions): this {
    const command = new AddCommand(this.strategy, data, options);
    command.execute();
    this.commandHistory.push(command);
    return this;
  }

  update(data: UpdateItem<T>, options?: UpdateOptions): this {
    const command = new UpdateCommand(this.strategy, data, options);
    command.execute();
    this.commandHistory.push(command);
    return this;
  }
}

export class InfiniteCacheManager<T extends Model> extends ListCacheManager<T> {
  constructor(options: CacheManagerOptions) {
    super(options);
  }

  getAll(): T[] | undefined {
    return this.strategy.getAll?.();
  }

  setPage(page: number): this {
    this.strategy.setPage?.(page);
    return this;
  }
}
